import { environment } from '../../../environments/environment';
import { ItemType, sellsAsIs } from '../models/item-type';

// Types mirroring source/backend Sage DTOs (BLL/Interfaces, BLL/DTO/Sage, DAL/Models)

export type SageEntityTypeName = 'SupplierCategory' | 'Supplier' | 'ItemCategory' | 'Item' | 'ItemBundle';

// Kebab-case slugs used in the frontend URL, mapped to backend enum names.
export const ENTITY_TYPE_ROUTE_MAP: Record<string, SageEntityTypeName> = {
  'supplier-category': 'SupplierCategory',
  supplier: 'Supplier',
  'item-category': 'ItemCategory',
  item: 'Item',
  'item-bundle': 'ItemBundle',
};

export const ENTITY_ROUTE_ORDER: { slug: string; type: SageEntityTypeName; label: string; icon: string; accent: string; accentSoft: string }[] = [
  { slug: 'supplier-category', type: 'SupplierCategory', label: 'Supplier Categories', icon: 'bi-tags', accent: '#6C7A3E', accentSoft: 'rgba(108,122,62,.12)' },
  { slug: 'supplier', type: 'Supplier', label: 'Suppliers', icon: 'bi-truck', accent: '#007a8f', accentSoft: 'rgba(0,122,143,.1)' },
  { slug: 'item-category', type: 'ItemCategory', label: 'Item Categories', icon: 'bi-collection', accent: '#26284F', accentSoft: 'rgba(38,40,79,.1)' },
  { slug: 'item', type: 'Item', label: 'Items', icon: 'bi-box-seam', accent: '#B0213C', accentSoft: 'rgba(176,33,60,.1)' },
  { slug: 'item-bundle', type: 'ItemBundle', label: 'Item Bundles', icon: 'bi-boxes', accent: '#0f766e', accentSoft: 'rgba(15,118,110,.1)' },
];

/** Reads which entities are visible in the Sage UI from runtime config (window.ENV, falling back to the build-time environment) — no rebuild needed to change this. */
export function getEnabledEntityRoutes(): typeof ENTITY_ROUTE_ORDER {
  const raw = (window as any).ENV?.SAGE_ENABLED_ENTITIES || (environment as any).sageEnabledEntities || '';
  const enabledSlugs: string[] = raw.split(',').map((s: string) => s.trim()).filter(Boolean);
  if (enabledSlugs.length === 0) return ENTITY_ROUTE_ORDER;
  return ENTITY_ROUTE_ORDER.filter((e) => enabledSlugs.includes(e.slug));
}

/** Mirrors SageAccess.Roles on the server. */
export const SAGE_ROLES = ['Admin User', 'Financial User'];

export function canUseSage(role: string | null | undefined): boolean {
  return !!role && SAGE_ROLES.includes(role);
}

export interface SageConnectionStatus {
  connected: boolean;
  status?: string; // 'connected' | 'disconnected' | 'error'
  syncEnabled: boolean;
  sageCompanyId?: string;
  sageCompanyName?: string;
  lastRefreshedAt?: string;
}

export interface SageCompany {
  id: number;
  name: string;
}

export interface SageReconciliationSession {
  id: number;
  tenantId?: number;
  entityType: string;
  status: string; // 'InProgress' | 'Confirmed' | 'Cancelled'
  createdAt?: string;
  confirmedAt?: string | null;
}

export interface SageReconciliationMatch {
  id: number;
  sessionId: number;
  aivorRecordId: number | null;
  sageRecordId: string | null;
  sageRecordSnapshot?: string | null;
  confidenceScore: number | null; // decimal(5,2), 0-100
  matchReason?: string | null;
  decision: SageDecision;
  resolvedAivorRecordId?: number | null;
  resolvedSageRecordId?: string | null;
  resolvedItemTypeID?: number | null;
  resolvedUOMID?: number | null;
  uomResolutionSource?: UomResolutionSource | null;
}

// The API sends the enum's number (no string-enum converter); the names are what this client writes locally.
export type UomResolutionSource = 'Exact' | 'Manual' | 0 | 1;

export function isManualUomSource(source: UomResolutionSource | null | undefined): boolean {
  return source === 'Manual' || source === 1;
}

export function isExactUomSource(source: UomResolutionSource | null | undefined): boolean {
  return source === 'Exact' || source === 0;
}

export type SageDecision =
  | 'Pending'
  | 'Confirmed'
  | 'Changed'
  | 'CreateInSage'
  | 'ImportToAivor'
  | 'Skipped'
  | 'NeedsUomMapping';

export type UiMappingStatus = 'confirmed' | 'review' | 'new-in-aivor' | 'new-in-sage';

/** Translates the backend's 6-value decision vocabulary onto the mockup's 4-value UI status. */
export function mapDecisionToUiStatus(decision: SageDecision): UiMappingStatus {
  switch (decision) {
    case 'Confirmed':
      return 'confirmed';
    case 'CreateInSage':
      return 'new-in-sage';
    case 'ImportToAivor':
      return 'new-in-aivor';
    case 'Pending':
    case 'Changed':
    case 'Skipped':
    case 'NeedsUomMapping':
    default:
      return 'review';
  }
}

export interface ReconciliationResult {
  sessionId: number;
  matches: SageReconciliationMatch[];
}

export interface UpdateMatchDecisionRequest {
  decision: SageDecision;
  resolvedAivorRecordId?: number | null;
  resolvedSageRecordId?: string | null;
  resolvedItemTypeID?: number | null;
}

export interface ResolveUomMappingRequest {
  purchaseUOMID: number;
}

// The PATCH overwrites every resolved field, so one left out is cleared rather than kept.
export function fullDecisionBody(
  match: SageReconciliationMatch,
  changes: Partial<UpdateMatchDecisionRequest>
): UpdateMatchDecisionRequest {
  return {
    decision: match.decision,
    resolvedAivorRecordId: match.resolvedAivorRecordId ?? null,
    resolvedSageRecordId: match.resolvedSageRecordId ?? null,
    resolvedItemTypeID: match.resolvedItemTypeID ?? null,
    ...changes,
  };
}

// Mirrors SageReconciliationRepository.ResolveUomMappingAsync: the row stays an import, and one
// waiting for its unit becomes an import again.
export function markUnitSaved(match: SageReconciliationMatch, uomId: number): void {
  match.resolvedUOMID = uomId;
  match.uomResolutionSource = 'Manual';
  if (match.decision === 'NeedsUomMapping') match.decision = 'ImportToAivor';
}

/** A manual override must not claim system confidence. 'near' (Sage's unit spelled another way,
 * m2 for m²) and 'guess' (pre-filled, not a match) are derived on the client, never stored. */
export type UomSource = 'exact' | 'near' | 'guess' | 'manual';

export interface BOMComponentWithSageInfo {
  bomComponentID: number;
  rawMaterialItemID: number;
  itemName: string | null;
  itemCode: string | null;
  hasSageItemId: boolean;
  syncStatus: 'Synced' | 'PendingSync' | 'Unmapped';
}

export interface BOMComponentsWithSageInfoResult {
  components: BOMComponentWithSageInfo[];
  componentCount: number;
  blockedComponentCount: number;
}

export interface UnitOfMeasureOption {
  id: number;
  code: string;
  name: string;
}

export interface SageUnitMatch {
  option: UnitOfMeasureOption;
  /** Mirrors SageUnitMatcher on the server. */
  kind: 'exact' | 'equivalent';
}

function exactUnitKey(value: string): string {
  return value.trim().toLowerCase();
}

// Sage and Aivor spell area and volume units differently (m2, M2, m²).
export function equivalentUnitKey(value: string): string {
  return exactUnitKey(value)
    .replace(/²/g, '2')
    .replace(/³/g, '3')
    .replace(/[\s.]/g, '');
}

export function matchSageUnit(sageUnit: string | null | undefined, options: UnitOfMeasureOption[]): SageUnitMatch | null {
  const unit = (sageUnit ?? '').trim();
  if (!unit) return null;
  const exactKey = exactUnitKey(unit);
  const exact = options.find((u) => exactUnitKey(u.code) === exactKey || exactUnitKey(u.name) === exactKey);
  if (exact) return { option: exact, kind: 'exact' };
  const key = equivalentUnitKey(unit);
  const equivalent = options.find((u) => equivalentUnitKey(u.code) === key || equivalentUnitKey(u.name) === key);
  return equivalent ? { option: equivalent, kind: 'equivalent' } : null;
}

export function toUnitOfMeasureOption(u: any): UnitOfMeasureOption {
  return {
    id: u.uomid ?? u.UOMID ?? u.id ?? 0,
    code: u.uomCode ?? u.UOMCode ?? u.code ?? '',
    name: u.uomName ?? u.UOMName ?? u.name ?? '',
  };
}

export interface ItemReportGroupOption {
  id: number;
  sageId: number | null;
  name: string;
}

export type ItemReportingGroupTypeId = 1 | 2 | 3 | 4;

export interface ItemReportGroup {
  id: number;
  sageItemReportingGroupId: number | null;
  description: string;
  itemReportingGroupTypeId: ItemReportingGroupTypeId;
  isActive: boolean;
}

export interface ReportGroupTypeMeta {
  id: ItemReportingGroupTypeId;
  label: string;
  caption: string;
  icon: string;
  fg: string; // pill text colour
  bg: string; // pill background
}

// Order here is the order of the filter chips.
export const REPORT_GROUP_TYPES: ReportGroupTypeMeta[] = [
  { id: 1, label: 'Sales',            caption: 'Revenue accounts',      icon: 'bi-cash-coin', fg: '#007a8f', bg: 'rgba(0,122,143,0.10)' },
  { id: 2, label: 'Purchases',        caption: 'Cost of sales',         icon: 'bi-bag-check', fg: '#3b4bb0', bg: '#eef1fb' },
  { id: 3, label: 'Inventory',        caption: 'Balance sheet stock',   icon: 'bi-box-seam',  fg: '#0f9d6f', bg: 'rgba(16,185,129,0.12)' },
  { id: 4, label: 'Item adjustments', caption: 'Write-offs and gains',  icon: 'bi-sliders',   fg: '#b45309', bg: 'rgba(245,158,11,0.14)' },
];

export function reportGroupType(id: number): ReportGroupTypeMeta {
  return REPORT_GROUP_TYPES.find((t) => t.id === id) ?? REPORT_GROUP_TYPES[0];
}

export interface TenantSageSettings {
  groupsLastSyncedAt?: string | null;
  defaultSalesReportingGroupID?: number | null;
}

export interface OverviewEntityResult {
  slug: string;
  sessionId: number | null;
  matches: SageReconciliationMatch[];
  /** Confirmed by an earlier first-sync run that stopped part-way; no new session was started. */
  confirmed?: boolean;
}

/** Everything the Sage overview screen needs, fetched once (during the connect animation) and
 * cached so navigating there doesn't repeat the status/onboarding-state/entity round-trips.
 * `entities` is null when onboarding is already completed (console takes over) or when
 * `loadError` is true — a partial/mixed result would render as a false "0 records" row. */
export interface OverviewBundle {
  status: SageConnectionStatus;
  onboardingCompleted: boolean;
  entities: OverviewEntityResult[] | null;
  loadError: boolean;
  loadErrorMessage: string;
}

export interface EntityCounts {
  matched: number;
  review: number;
  newInAivor: number;
  newInSage: number;
  total: number;
}

export function computeEntityCounts(allMatches: SageReconciliationMatch[]): EntityCounts {
  const rows = allMatches.map((match) => ({ match }));
  const matches = rows.filter((r) => !isClaimedAivorOnlyRow(r, rows)).map((r) => r.match);
  const counts: EntityCounts = { matched: 0, review: 0, newInAivor: 0, newInSage: 0, total: matches.length };
  for (const m of matches) {
    switch (mapDecisionToUiStatus(m.decision)) {
      case 'confirmed':
        counts.matched++;
        break;
      case 'review':
        counts.review++;
        break;
      case 'new-in-aivor':
        counts.newInAivor++;
        break;
      case 'new-in-sage':
        counts.newInSage++;
        break;
    }
  }
  return counts;
}

export interface FieldMapping {
  aivorLabel: string;
  /** Candidate keys to try against the AIVOR record, in order — first defined/non-empty wins. Dotted paths supported (e.g. "Category.Id"). */
  aivorKeys: string[];
  sageLabel: string;
  /** Candidate keys to try against the parsed Sage snapshot, in order. */
  sageKeys: string[];
}

/** Field mapping per entity type — mirrors the AIVOR<->Sage cross-reference spec used by the sync engine. */
export const FIELD_MAPPINGS: Record<SageEntityTypeName, FieldMapping[]> = {
  SupplierCategory: [
    { aivorLabel: 'Description', aivorKeys: ['description', 'categoryName', 'name'], sageLabel: 'Description', sageKeys: ['Description', 'Name'] },
    { aivorLabel: 'SageSupplierCategoryId', aivorKeys: ['sageSupplierCategoryId', 'sageSupplierCategoryID'], sageLabel: 'ID', sageKeys: ['Id', 'ID'] },
  ],
  Supplier: [
    { aivorLabel: 'CompanyName', aivorKeys: ['companyName', 'name', 'supplierName'], sageLabel: 'Name', sageKeys: ['Name'] },
    { aivorLabel: 'SageSupplierId', aivorKeys: ['sageSupplierId'], sageLabel: 'ID', sageKeys: ['Id', 'ID'] },
    { aivorLabel: 'SupplierCategoryID', aivorKeys: ['supplierCategoryID', 'categoryID'], sageLabel: 'Category.ID', sageKeys: ['Category.Id', 'CategoryId'] },
    { aivorLabel: 'PrimaryContactPerson', aivorKeys: ['primaryContactPerson', 'contactName'], sageLabel: 'ContactName', sageKeys: ['ContactName'] },
    { aivorLabel: 'PrimaryPhone', aivorKeys: ['primaryPhone', 'telephone', 'phone'], sageLabel: 'Telephone', sageKeys: ['Telephone'] },
    { aivorLabel: 'PrimaryEmail', aivorKeys: ['primaryEmail', 'email'], sageLabel: 'Email', sageKeys: ['Email'] },
  ],
  ItemCategory: [
    { aivorLabel: 'CategoryID', aivorKeys: ['categoryID', 'id'], sageLabel: 'ID', sageKeys: ['Id', 'ID'] },
    { aivorLabel: 'CategoryName', aivorKeys: ['categoryName', 'description', 'name'], sageLabel: 'Description', sageKeys: ['Description', 'Name'] },
  ],
  Item: [
    { aivorLabel: 'ItemCode', aivorKeys: ['itemCode', 'code'], sageLabel: 'Code', sageKeys: ['Code'] },
    { aivorLabel: 'ItemName / Description', aivorKeys: ['itemName', 'description', 'name'], sageLabel: 'Description', sageKeys: ['Description'] },
    { aivorLabel: 'CategoryID', aivorKeys: ['categoryID'], sageLabel: 'Category', sageKeys: ['Category.Id', 'CategoryId', 'Category'] },
  ],
  ItemBundle: [
    { aivorLabel: 'ItemCode', aivorKeys: ['itemCode'], sageLabel: 'BundleCode', sageKeys: ['BundleCode'] },
    { aivorLabel: 'ItemName', aivorKeys: ['itemName'], sageLabel: 'Description', sageKeys: ['Description'] },
  ],
};

/** Case-insensitive, dotted-path field lookup — tries each candidate key in order and returns the first non-empty value. */
export function getFieldValue(obj: any, keys: string[]): any {
  if (!obj) return undefined;
  for (const key of keys) {
    let value: any = obj;
    for (const segment of key.split('.')) {
      if (value == null) break;
      const matchedKey = Object.keys(value).find((k) => k.toLowerCase() === segment.toLowerCase());
      value = matchedKey ? value[matchedKey] : undefined;
    }
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

// Per entity because item records also carry categoryID, which a shared key list can pick up first.
const AIVOR_RECORD_ID_KEY: Record<SageEntityTypeName, string> = {
  SupplierCategory: 'supplierCategoryID',
  Supplier: 'supplierID',
  ItemCategory: 'categoryId',
  Item: 'itemID',
  ItemBundle: 'itemID',
};

export function aivorRecordIdFor(entityType: SageEntityTypeName, record: any): number | undefined {
  return getFieldValue(record, [AIVOR_RECORD_ID_KEY[entityType]]);
}

export function aivorRecordDisplay(entityType: SageEntityTypeName, r: any): { name: string; meta: string } {
  const name = r.name ?? r.supplierName ?? r.companyName ?? r.itemName ?? r.categoryName ?? r.description ?? '—';
  // Suppliers have no code; their email tells same-named ones apart and is searchable.
  let meta = r.code ?? r.itemCode ?? r.supplierCode ?? r.primaryEmail ?? '';
  if (entityType === 'ItemBundle') {
    const itemCode = r.itemCode ?? r.ItemCode ?? '';
    const bomVersion = r.bundleInfo?.bomVersion;
    meta = bomVersion ? `${itemCode} · BOM v${bomVersion} active` : itemCode;
  }
  return { name, meta };
}

// An import row creates a new Aivor record, so a matcher suggestion left on aivorRecordId isn't its counterpart.
export function effectiveAivorRecordId(m: SageReconciliationMatch): number | null {
  if (m.decision === 'ImportToAivor' || m.decision === 'NeedsUomMapping') return null;
  return m.resolvedAivorRecordId ?? m.aivorRecordId ?? null;
}

// Pending counts because "Confirm all shown" would confirm it; CreateInSage rows are the Aivor
// record's own unmatched row, which is exactly what a Sage row should be able to claim.
const CLAIMING_DECISIONS: SageDecision[] = ['Confirmed', 'Changed', 'Pending'];

export function claimedAivorRecordId(m: SageReconciliationMatch): number | null {
  return m.sageRecordId && CLAIMING_DECISIONS.includes(m.decision) ? (m.resolvedAivorRecordId ?? m.aivorRecordId ?? null) : null;
}

// Carry-forward appends " | carried from s#N" for its own bookkeeping, not for the user.
export function matchReasonText(m: SageReconciliationMatch): string | null {
  return m.matchReason?.split(' | carried from')[0] || null;
}

export function findClaimingRow<T extends { match: SageReconciliationMatch }>(
  rows: T[],
  aivorId: number,
  excludeMatchId: number
): T | undefined {
  return rows.find((r) => r.match.id !== excludeMatchId && claimedAivorRecordId(r.match) === aivorId);
}

export function isClaimedAivorOnlyRow<T extends { match: SageReconciliationMatch }>(row: T, rows: T[]): boolean {
  const m = row.match;
  return !m.sageRecordId && m.decision === 'CreateInSage' && m.aivorRecordId != null && !!findClaimingRow(rows, m.aivorRecordId, m.id);
}

const DEFAULT_LOAD_ERROR_MESSAGE =
  "We couldn't load this data. Your connection may have dropped or Sage may be unreachable.";

/** Mirrors BLL.Exceptions.SageErrorCategory — set on the { category, message } body the backend
 * returns for classified Sage failures (SageController/SageReconciliationController). */
const LOAD_ERROR_MESSAGES_BY_CATEGORY: Record<string, string> = {
  AuthenticationFailed: 'Your Sage connection has expired. Please reconnect Sage.',
  Forbidden: "Sage rejected this request due to a permissions issue. Please check your Sage account's Add-On access.",
  RateLimited:
    'Sage has temporarily blocked this organisation for exceeding its request limit. Sync pauses automatically and resumes within the hour — no action is needed.',
  LoginBlocked:
    'Sage has blocked sign-in for 24 hours after too many failed attempts. Sync is paused until then. Please contact support.',
  SageServerError: 'Sage is currently unavailable. Please try again shortly.',
  GatewayTimeout: 'Sage took too long to respond. Please try again shortly.',
  NetworkFailure: "We couldn't reach Sage. Please check your connection and try again.",
};

function categoryMessageFor(err: any): string | null {
  const category = err?.error?.category;
  return (category && LOAD_ERROR_MESSAGES_BY_CATEGORY[category]) || null;
}

/** Picks a user-facing message for a failed getSession/startReconciliation call — uses the
 * backend's classified `category` field when present, else falls back to a generic message. */
export function loadErrorMessageFor(err: any): string {
  return categoryMessageFor(err) ?? DEFAULT_LOAD_ERROR_MESSAGE;
}

/** Keyed by the `reason` SageController.Callback adds to `?sage=error`. */
const SIGN_IN_ERROR_MESSAGES: Record<string, string> = {
  cancelled: "The Sage sign-in was cancelled, so Aivor isn't connected. Please try again when you're ready.",
  expired: 'The Sage sign-in took too long and timed out. Please try again.',
  login_blocked: 'Sage has blocked sign-in for 24 hours after too many failed attempts. Please contact support.',
  unavailable: LOAD_ERROR_MESSAGES_BY_CATEGORY['SageServerError'],
};

export const DEFAULT_SIGN_IN_ERROR_MESSAGE =
  'Something went wrong while connecting to Sage. Please try again. If it keeps happening, please contact support.';

export function signInErrorMessageFor(reason: string | null | undefined): string {
  // hasOwnProperty: the reason comes from the address bar, and ?reason=constructor would find Object's.
  return reason && Object.prototype.hasOwnProperty.call(SIGN_IN_ERROR_MESSAGES, reason)
    ? SIGN_IN_ERROR_MESSAGES[reason]
    : DEFAULT_SIGN_IN_ERROR_MESSAGE;
}

/** The `status` in the 409 body the backend returns when a tenant has sync paused. */
export const SAGE_SYNC_DISABLED_STATUS = 'SyncDisabled';

const DEFAULT_SYNC_DISABLED_MESSAGE = 'Sage sync is paused for this organisation.';

export function isSyncDisabledError(err: any): boolean {
  return err?.status === 409 && err?.error?.status === SAGE_SYNC_DISABLED_STATUS;
}

/** The `status` in the 503 body the trigger endpoint returns when too many manual syncs are queued. */
const SAGE_QUEUE_FULL_STATUS = 'QueueFull';

/** The `status` in the 409 body when a sync, or another apply, already holds the organisation's sync lock. */
const SAGE_ALREADY_RUNNING_STATUS = 'AlreadyRunning';

/** Message for a failed confirm/trigger call, or null when the failure carries nothing better than
 * the caller's own fallback wording. */
export function sageErrorMessageFor(err: any): string | null {
  if (isSyncDisabledError(err)) {
    return err?.error?.message || DEFAULT_SYNC_DISABLED_MESSAGE;
  }
  // A review's apply is refused while a sync holds the lock; the trigger handles its own 409 first.
  if (err?.status === 409 && err?.error?.status === SAGE_ALREADY_RUNNING_STATUS) {
    return err.error.message || null;
  }
  // Matched on the body, not just the 503: a gateway 503 during a deploy carries no message worth showing.
  if (err?.status === 503 && err?.error?.status === SAGE_QUEUE_FULL_STATUS) {
    return err.error.message || null;
  }
  return categoryMessageFor(err);
}

export interface BundleInfo {
  bomVersion: string | null;
  componentCount: number;
  blockedComponentCount: number;
  componentItemIds: number[];
}

const localDateKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** CurrentBom.IsValidOn on the server. Dates are compared by their YYYY-MM-DD part, so a bill dated
 * any time today is already valid and one ending today still is. */
export function isBomValidOn(header: any, today: Date): boolean {
  if (!(header.isActive ?? header.IsActive)) return false;
  const todayKey = localDateKey(today);
  const validFrom = header.validFrom ?? header.ValidFrom;
  const validTo = header.validTo ?? header.ValidTo;
  return (!validFrom || String(validFrom).slice(0, 10) <= todayKey) && (!validTo || String(validTo).slice(0, 10) >= todayKey);
}

/** The bill a finished good goes to Sage with (FS Open Item 12), as CurrentBom.Pick: valid today,
 * the latest valid from first, then the highest id. ISO strings without an offset sort as dates. */
export function currentBomHeader(bomHeaders: any[], finishedGoodItemId: number, today: Date = new Date()): any | undefined {
  return bomHeaders
    .filter((h) => (h.finishedGoodItemID ?? h.FinishedGoodItemID) === finishedGoodItemId && isBomValidOn(h, today))
    .sort((a, b) => {
      const fromA = String(a.validFrom ?? a.ValidFrom ?? '');
      const fromB = String(b.validFrom ?? b.ValidFrom ?? '');
      if (fromA !== fromB) return fromA < fromB ? 1 : -1;
      return (b.bomHeaderID ?? b.BOMHeaderID) - (a.bomHeaderID ?? a.BOMHeaderID);
    })[0];
}

/** Derives the fields the Item Bundles table needs from raw BOMHeaders/BOMComponents/Items lists
 * — the bill from currentBomHeader; a component is "blocked" (SAGE.BDL.007) when its raw material
 * item has no SageItemId yet. */
export function computeBundleInfo(
  finishedGoodItemId: number,
  bomHeaders: any[],
  bomComponents: any[],
  allItems: any[],
  today: Date = new Date()
): BundleInfo {
  const activeHeader = currentBomHeader(bomHeaders, finishedGoodItemId, today);

  if (!activeHeader) {
    return { bomVersion: null, componentCount: 0, blockedComponentCount: 0, componentItemIds: [] };
  }

  const headerId = activeHeader.bomHeaderID ?? activeHeader.BOMHeaderID;
  const components = bomComponents.filter((c) => (c.bomHeaderID ?? c.BOMHeaderID) === headerId);
  const componentItemIds: number[] = components.map((c) => c.rawMaterialItemID ?? c.RawMaterialItemID);
  const blockedComponentCount = componentItemIds.filter((rawMaterialId) => {
    const rawMaterialItem = allItems.find((i) => (i.itemID ?? i.ItemID) === rawMaterialId);
    return !(rawMaterialItem?.sageItemId ?? rawMaterialItem?.SageItemId);
  }).length;

  return {
    bomVersion: activeHeader.bomVersion ?? activeHeader.BOMVersion ?? null,
    componentCount: components.length,
    blockedComponentCount,
    componentItemIds,
  };
}

export type FinishedGoodSyncTarget = 'Bundle' | 'Standalone';

/** Mirrors SageItemPayloadHelpers.EffectiveSyncTargetType. /api/Items/GetAll sends the enum as a number. */
export function finishedGoodSyncTarget(item: any): FinishedGoodSyncTarget | null {
  const itemTypeID = item?.itemTypeID ?? item?.ItemTypeID;
  if (sellsAsIs({ itemTypeID, isBuyout: item?.isBuyout ?? item?.IsBuyout })) return null;
  const saved = item?.syncTargetType ?? item?.SyncTargetType;
  if (saved === 1 || saved === 'Bundle') return 'Bundle';
  if (saved === 2 || saved === 'Standalone') return 'Standalone';
  return itemTypeID === ItemType.FinishedGood ? 'Standalone' : null;
}

/** Each choice keeps its own Sage link, so switching a good that was sent under its current choice
 * leaves that Sage record behind, still active. */
export function leavesOldSageRecord(
  current: FinishedGoodSyncTarget | null,
  next: FinishedGoodSyncTarget,
  sageItemId: number | null | undefined,
  sageBundleId: number | null | undefined
): boolean {
  if (!current || current === next) return false;
  return current === 'Standalone' ? !!sageItemId : !!sageBundleId;
}

// ---- Console (post-onboarding) types ----
// Mirrors DAL.Models.SageSyncLog/SageSyncError/SageConflictLog* — these enums have no
// JsonStringEnumConverter configured, so the backend serializes them as their underlying ints.

const SAGE_ENTITY_TYPE_BY_NUMBER: Record<number, SageEntityTypeName> = {
  1: 'Supplier',
  2: 'SupplierCategory',
  3: 'Item',
  4: 'ItemCategory',
  5: 'ItemBundle',
  // InventoryTransaction: inventory adjustments sync as part of the Item entity, not a
  // syncable entity of their own — map to Item so any log/error rows still label as "Items".
  6: 'Item',
};

const INVENTORY_TRANSACTION_ENTITY_TYPE = 6;

export function entityTypeName(n: number): SageEntityTypeName {
  return SAGE_ENTITY_TYPE_BY_NUMBER[n];
}

// Reviews confirm in this order so categories exist before the records that use them, and items
// before the bundles built from them.
export function entityApplyOrder(n: number): number {
  const index = ENTITY_ROUTE_ORDER.findIndex((e) => e.type === entityTypeName(n));
  return index === -1 ? ENTITY_ROUTE_ORDER.length : index;
}

export function entityLabel(n: number): string {
  const type = entityTypeName(n);
  return ENTITY_ROUTE_ORDER.find((e) => e.type === type)?.label ?? String(type ?? n);
}

const DEFAULT_ENTITY_ICON = 'bi-box-seam';
const DEFAULT_ENTITY_ACCENT = '#007a8f';
const DEFAULT_ENTITY_ACCENT_SOFT = 'rgba(0,122,143,.1)';

/** Same icon/colour lookup the overview screen uses (ENTITY_ROUTE_ORDER) — keyed off the raw
 * numeric EntityType the console's endpoints return, rather than the route slug. */
export function entityIcon(n: number): string {
  return entityIconForType(entityTypeName(n));
}

export function entityAccent(n: number): string {
  return entityAccentForType(entityTypeName(n));
}

export function entityAccentSoft(n: number): string {
  return entityAccentSoftForType(entityTypeName(n));
}

/** String-keyed counterparts of entityIcon/entityAccent/entityAccentSoft, for call sites that
 * already have the SageEntityTypeName string (e.g. off a DTO) rather than the numeric EntityType. */
export function entityLabelForType(type: SageEntityTypeName | string): string {
  return ENTITY_ROUTE_ORDER.find((e) => e.type === type)?.label ?? String(type);
}

export function entityIconForType(type: SageEntityTypeName | string): string {
  return ENTITY_ROUTE_ORDER.find((e) => e.type === type)?.icon ?? DEFAULT_ENTITY_ICON;
}

export function entityAccentForType(type: SageEntityTypeName | string): string {
  return ENTITY_ROUTE_ORDER.find((e) => e.type === type)?.accent ?? DEFAULT_ENTITY_ACCENT;
}

export function entityAccentSoftForType(type: SageEntityTypeName | string): string {
  return ENTITY_ROUTE_ORDER.find((e) => e.type === type)?.accentSoft ?? DEFAULT_ENTITY_ACCENT_SOFT;
}

/** Null for InventoryTransaction: entityTypeName aliases it onto 'Item' so log rows still label
 * as "Items", but as a link target that would send a reviewer to the wrong screen. */
export function entitySlug(n: number): string | null {
  if (n === INVENTORY_TRANSACTION_ENTITY_TYPE) {
    return null;
  }
  const type = entityTypeName(n);
  return ENTITY_ROUTE_ORDER.find((e) => e.type === type)?.slug ?? null;
}

export interface SageConfirmResult {
  sessionId: number;
  status: string;
  successorSessionId?: number | null;
  carriedForwardCount?: number;
  alreadyConfirmed?: boolean;
}

/** POST {sessionId}/confirm-selected. A blocked row stays in the review; `message` says why. */
export interface SageConfirmSelectedResult {
  sessionId: number;
  applied: number;
  blocked: { matchId: number; errorCode: string; message: string }[];
}

export interface SagePendingReviewEntity {
  entityType: number;
  sessionId: number;
  sessionCreatedAt: string;
  reviewCount: number;
}

export interface SagePendingReviewResult {
  totalCount: number;
  entities: SagePendingReviewEntity[];
}

const MINUTE_MS = 60_000;
const HOUR_MS = 3600_000;
const DAY_MS = 24 * HOUR_MS;
const STALE_DAYS = 2;

function ageMs(iso: string): number | null {
  const then = Date.parse(iso);
  if (isNaN(then)) {
    return null;
  }
  // A clock skew between the server's UTC stamp and the browser would otherwise read as negative.
  return Math.max(0, Date.now() - then);
}

/** "just now" | "12 minutes" | "6 hours" | "2 days" — how long a review session has been waiting. */
export function ageLabel(iso: string): string {
  const elapsed = ageMs(iso);
  if (elapsed === null) {
    return '';
  }

  if (elapsed < MINUTE_MS) {
    return 'just now';
  }

  if (elapsed < HOUR_MS) {
    const minutes = Math.floor(elapsed / MINUTE_MS);
    return minutes === 1 ? '1 minute' : `${minutes} minutes`;
  }

  if (elapsed < DAY_MS) {
    const hours = Math.floor(elapsed / HOUR_MS);
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }

  const days = Math.floor(elapsed / DAY_MS);
  return days === 1 ? '1 day' : `${days} days`;
}

export function isStale(iso: string): boolean {
  const elapsed = ageMs(iso);
  return elapsed !== null && elapsed >= STALE_DAYS * DAY_MS;
}

export type SyncDirection = 'Outbound' | 'Inbound';
export type SyncTrigger = 'Scheduled' | 'RequestTriggered' | 'ReviewApplied';
export type SyncRunStatus = 'Running' | 'Succeeded' | 'PartialFailure' | 'Failed';

const SYNC_DIRECTION_BY_NUMBER: Record<number, SyncDirection> = { 1: 'Outbound', 2: 'Inbound' };
const SYNC_TRIGGER_BY_NUMBER: Record<number, SyncTrigger> = { 1: 'Scheduled', 2: 'RequestTriggered', 3: 'ReviewApplied' };
const SYNC_STATUS_BY_NUMBER: Record<number, SyncRunStatus> = { 1: 'Running', 2: 'Succeeded', 3: 'PartialFailure', 4: 'Failed' };

export function syncDirectionName(n: number): SyncDirection { return SYNC_DIRECTION_BY_NUMBER[n]; }
export function syncTriggerName(n: number): SyncTrigger { return SYNC_TRIGGER_BY_NUMBER[n]; }
export function syncStatusName(n: number): SyncRunStatus { return SYNC_STATUS_BY_NUMBER[n]; }

const SYNC_TRIGGER_LABEL: Record<SyncTrigger, string> = { Scheduled: 'Scheduled', RequestTriggered: 'Manual', ReviewApplied: 'Review applied' };

export function syncTriggerLabel(n: number): string { return SYNC_TRIGGER_LABEL[syncTriggerName(n)] ?? 'Scheduled'; }

const SYNC_DIRECTION_LABEL: Record<SyncDirection, string> = { Outbound: 'To Sage', Inbound: 'From Sage' };
const SYNC_DIRECTION_ICON: Record<SyncDirection, string> = {
  Outbound: 'bi-box-arrow-up',
  Inbound: 'bi-box-arrow-in-down',
};

export function syncDirectionLabel(n: number): string {
  return SYNC_DIRECTION_LABEL[syncDirectionName(n)] ?? '';
}

export function syncDirectionIcon(n: number): string {
  return SYNC_DIRECTION_ICON[syncDirectionName(n)] ?? '';
}

/** entityLabel folds stock reconciliation into "Items", which in run history reads as a duplicate
 * of the item push. Only the run history needs it split out. */
export function syncRunEntityLabel(n: number): string {
  return n === INVENTORY_TRANSACTION_ENTITY_TYPE ? 'Items (stock)' : entityLabel(n);
}

export interface SageSyncLog {
  id: number;
  entityType: number;
  direction: number;
  trigger: number;
  status: number;
  startedAt: string;
  completedAt?: string | null;
  recordsProcessed: number;
  recordsSucceeded: number;
  recordsFailed: number;
  recordsSkipped: number;
  errorSummary?: string | null;
  errorCategory?: string | null;
}

export interface SageSyncEntityStatus {
  entityType: number;
  outbound: SageSyncLog | null;
  inbound: SageSyncLog | null;
}

export interface SyncStatusResponse {
  isSyncing: boolean;
  entities: SageSyncEntityStatus[];
}

const SYNC_OUTBOUND = 1;
const SYNC_INBOUND = 2;
const SYNC_RUNNING = 1;
const SYNC_SUCCEEDED = 2;
const SYNC_PARTIAL = 3;
const SYNC_FAILED = 4;

/** Latest log row id per entity + direction, taken just before a sync is requested. */
export type SyncBaseline = Record<string, number | null>;

function syncBaselineKey(entityType: number, direction: number): string {
  return `${entityType}:${direction}`;
}

export function syncBaseline(entities: SageSyncEntityStatus[]): SyncBaseline {
  const baseline: SyncBaseline = {};
  for (const e of entities) {
    baseline[syncBaselineKey(e.entityType, SYNC_OUTBOUND)] = e.outbound?.id ?? null;
    baseline[syncBaselineKey(e.entityType, SYNC_INBOUND)] = e.inbound?.id ?? null;
  }
  return baseline;
}

/** The status endpoint has no run id, so a row counts as this run's once it's no longer the row
 * the baseline saw. That also keeps a crashed earlier run's row, stuck at Running, out of it. */
export function isRowFromRun(row: SageSyncLog | null | undefined, baseline: SyncBaseline): row is SageSyncLog {
  return !!row && row.id !== (baseline[syncBaselineKey(row.entityType, row.direction)] ?? null);
}

export type SyncStepStatus = 'done' | 'syncing' | 'queued' | 'failed' | 'notRun';

export interface SyncStep {
  entityType: number;
  label: string;
  status: SyncStepStatus;
}

export interface SyncProgress {
  steps: SyncStep[];
  /** -1 when no step is syncing. */
  currentIndex: number;
  /** Every push step has resolved but the run is still going — the stock reconcile and inbound pull. */
  pullPhase: boolean;
  anyFailed: boolean;
}

/**
 * The steps follow the outbound push, since the backend pushes every entity before pulling any.
 * A failed pull still marks its entity failed. The first unresolved step reads as syncing even
 * before its log row appears, so the stepper doesn't flicker back to queued between steps; after
 * the run ends, unresolved steps never ran (a thrown step error stops the rest of the run).
 */
export function buildSyncSteps(
  entities: SageSyncEntityStatus[],
  baseline: SyncBaseline,
  run: { started: boolean; ended: boolean }
): SyncProgress {
  const ordered = ENTITY_ROUTE_ORDER
    .map((r) => entities.find((e) => entityTypeName(e.entityType) === r.type))
    .filter((e): e is SageSyncEntityStatus => !!e);

  const resolved = ordered.map((e): 'done' | 'failed' | null => {
    const outbound = isRowFromRun(e.outbound, baseline) ? e.outbound : null;
    const inbound = isRowFromRun(e.inbound, baseline) ? e.inbound : null;
    if (outbound?.status === SYNC_FAILED || inbound?.status === SYNC_FAILED) return 'failed';
    if (outbound && outbound.status !== SYNC_RUNNING) return 'done';
    return null;
  });

  const firstOpen = resolved.indexOf(null);
  const live = run.started && !run.ended;
  const currentIndex = live ? firstOpen : -1;

  return {
    steps: ordered.map((e, i) => ({
      entityType: e.entityType,
      label: entityLabel(e.entityType),
      status: resolved[i] ?? (i === currentIndex ? 'syncing' : run.ended ? 'notRun' : 'queued'),
    })),
    currentIndex,
    pullPhase: live && firstOpen === -1,
    anyFailed: resolved.includes('failed'),
  };
}

/** A step's row reads Running with zero counts and no completedAt until the step ends, which would
 * blank the entity card mid-sync, so the last finished row stays on show until then. */
export function mergeLiveStatuses(prev: SageSyncEntityStatus[], live: SageSyncEntityStatus[]): SageSyncEntityStatus[] {
  return live.map((l) => {
    const p = prev.find((x) => x.entityType === l.entityType);
    if (!p) return l;
    return {
      ...l,
      outbound: l.outbound?.status === SYNC_RUNNING ? p.outbound : l.outbound,
      inbound: l.inbound?.status === SYNC_RUNNING ? p.inbound : l.inbound,
    };
  });
}

/** When records last synced: the newest finish among the latest runs that synced anything. */
export function latestSyncedAt(entities: SageSyncEntityStatus[]): string | null {
  let latest: string | null = null;
  for (const run of entities.flatMap((e) => [e.outbound, e.inbound])) {
    if (!run?.completedAt || (run.status !== SYNC_SUCCEEDED && run.status !== SYNC_PARTIAL)) continue;
    if (latest === null || Date.parse(run.completedAt) > Date.parse(latest)) latest = run.completedAt;
  }
  return latest;
}

/** The backend returns its enum order; the cards follow the order records are matched in. */
export function inMatchingOrder(statuses: SageSyncEntityStatus[]): SageSyncEntityStatus[] {
  return [...statuses].sort((a, b) => entityApplyOrder(a.entityType) - entityApplyOrder(b.entityType));
}

export interface SageSyncError {
  id: number;
  sageSyncLogId: number;
  recordId?: string | null;
  recordSource?: number | null;
  errorCode?: string | null;
  errorMessage: string;
  occurredAt: string;
}

export interface SageConflictLogFieldRow {
  id: number;
  fieldName: string;
  aivorValue?: string | null;
  sageValue?: string | null;
}

export interface SageConflictLog {
  id: number;
  entityType: number;
  aivorRecordId: number;
  recordDisplayName?: string | null;
  winningSystem: number; // always 1 (Aivor) today — Sage-wins resolution is out of scope
  reason: string;
  detectedAt: string;
  fields: SageConflictLogFieldRow[];
}

// Mirrors DAL.Models.SageConflictWinningSystem.
const CONFLICT_WINNING_SYSTEM_BY_NUMBER: Record<number, string> = { 1: 'Aivor', 2: 'Sage', 3: 'Manual' };

export function winningSystemName(n: number): string {
  return CONFLICT_WINNING_SYSTEM_BY_NUMBER[n] ?? 'Unknown';
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
}

// null is an ellipsis. Fixed at seven slots so the arrows don't shift under the cursor while paging.
export function pageWindow(current: number, total: number): (number | null)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, null, total];
  if (current >= total - 3) return [1, null, total - 4, total - 3, total - 2, total - 1, total];
  return [1, null, current - 1, current, current + 1, null, total];
}

// Mirrors DAL.Models.SageItemAdjustment — one pending stock correction for an item whose
// quantity differs between Aivor and Sage. Nothing is sent to Sage until a reason is attached.
export interface SageItemAdjustment {
  id: number;
  itemId: number;
  itemCode?: string | null;
  itemName?: string | null;
  direction: number;
  aivorQuantity: number;
  sageQuantity: number;
  delta: number;
  status: number;
  detectedAt: string;
  reasonId?: number | null;
  reasonDescription?: string | null;
  releasedAt?: string | null;
  releasedBy?: string | null;
  lastError?: string | null;
}

export interface SageItemAdjustmentReason {
  id: number;
  description: string;
}

export interface StockAdjustmentFilter {
  status?: string;
  page?: number;
  pageSize?: number;
}

// Mirrors DAL.Models.SageStockCorrectionDirection — which system is being brought into line.
const STOCK_CORRECTION_DIRECTION_BY_NUMBER: Record<number, string> = { 1: 'Sage', 2: 'Aivor' };

export function stockCorrectionTargetName(n: number): string {
  return STOCK_CORRECTION_DIRECTION_BY_NUMBER[n] ?? 'Unknown';
}

// Mirrors DAL.Models.SageItemAdjustmentStatus.
const SAGE_ITEM_ADJUSTMENT_STATUS_BY_NUMBER: Record<number, string> = {
  1: 'Pending',
  2: 'Applying',
  3: 'Applied',
  4: 'Failed',
  5: 'Cancelled',
};

export function stockAdjustmentStatusName(n: number): string {
  return SAGE_ITEM_ADJUSTMENT_STATUS_BY_NUMBER[n] ?? 'Unknown';
}

// Mirrors DAL.DTO.SageSyncErrorDTO, returned by GET /api/sage/sync/errors — enum-like fields here
// are already-resolved strings (unlike SageSyncError above, which mirrors the raw table/int enums
// used by getSyncLogById).
export interface SageSyncErrorRow {
  id: number;
  syncLogId: number;
  occurredAt: string;
  entityType: string;
  direction: string;
  syncTrigger: string;
  syncStartedAt: string;
  recordId?: string | null;
  recordSource?: string | null;
  recordLabel: string;
  recordDisplayName?: string | null;
  aivorRecordId?: number | null;
  recordExistsLocally: boolean;
  /** Set only by GET errors. */
  isResolved?: boolean;
  category: string;
  errorCode?: string | null;
  errorMessage: string;
  sageStatusCode?: number | null;
  sageRoute?: string | null;
  hasResponseBody: boolean;
  sageResponseBody?: string | null;
}

export interface SageSyncErrorCategoryCount {
  category: string;
  count: number;
}

export interface SageSyncErrorPage {
  items: SageSyncErrorRow[];
  filteredCount: number;
  totalCount: number;
  categoryCounts: SageSyncErrorCategoryCount[];
}

// The newest run per entity type and direction, which is what "latest" scopes to — there is no
// sweep id joining a run's per-entity logs together.
export interface SageLatestSessionErrorPage extends SageSyncErrorPage {
  runs: SageSyncLog[];
}

export interface SageSyncErrorCodeCount {
  errorCode: string;
  count: number;
}

// One entry per broken record, not per sweep that re-reported it — a blocked record is re-written
// on every sweep until it is fixed.
export interface SageActionableError {
  latest: SageSyncErrorRow;
  occurrences: number;
  /** Earliest occurrence inside the response's window, not necessarily the true first. */
  firstSeenAt: string;
  lastSeenAt: string;
  /** 'sweep' re-proves itself every run; 'reconciliation' was recorded once at confirm time. */
  livenessBasis: string;
}

export interface SageSyncErrorEntityCount {
  entityType: string;
  count: number;
}

export interface SageActionableErrorPage {
  items: SageActionableError[];
  totalCount: number;
  windowStart: string;
  codeCounts: SageSyncErrorCodeCount[];
  /** Per record type, from the whole list rather than one page. */
  entityCounts?: SageSyncErrorEntityCount[];
}

// ---- Shared issue-row view model ----
// Sync errors (a record Sage rejected) and conflict logs (Sage values that Aivor's send
// replaced) are unrelated sources, mapped onto one shape so both lists share the
// entity label/icon lookups and trackBy.

export type SageIssueType = 'error' | 'conflict';

export interface SageIssueRow {
  type: SageIssueType;
  /** Unique across both sources — row identity for trackBy and for which row is expanded. */
  key: string;
  id: number;
  /** String form, so both sources share one entity label/icon lookup. */
  entityType: string;
  recordLabel: string;
  /** Error code or category; empty for conflicts, which carry no reason code. */
  reason: string;
  /** API codes like `BadRequest` read as a failure and get the red treatment; a normalised
   * category such as `missing_data` is just a bucket and stays muted. */
  reasonIsApiCode: boolean;
  detectedAt: string;
  error?: SageSyncErrorRow;
  conflict?: SageConflictLog;
}

export function issueFromError(row: SageSyncErrorRow): SageIssueRow {
  return {
    type: 'error',
    key: `error:${row.id}`,
    id: row.id,
    entityType: row.entityType,
    recordLabel: row.recordLabel,
    reason: issueReasonLabel(row.errorCode, row.category),
    reasonIsApiCode: !!row.errorCode,
    detectedAt: row.occurredAt,
    error: row,
  };
}

/** `latest` is an unchanged SageSyncErrorRow, so the shared row template needs no variant. */
export function issueFromActionable(row: SageActionableError): SageIssueRow {
  return issueFromError(row.latest);
}

export function issueFromConflict(row: SageConflictLog): SageIssueRow {
  return {
    type: 'conflict',
    key: `conflict:${row.id}`,
    id: row.id,
    entityType: entityTypeName(row.entityType),
    recordLabel: row.recordDisplayName || `Record #${row.aivorRecordId}`,
    reason: '',
    reasonIsApiCode: false,
    detectedAt: row.detectedAt,
    conflict: row,
  };
}

export interface SageSyncProvenance {
  entityType: number;
  aivorRecordId: number;
  sageLastSyncedAt?: string | null;
  sageLastPushedAt?: string | null;
  sageLastPayloadHash?: string | null;
}

export type ProvenanceSyncState = 'in-sync' | 'push-pending' | 'never-synced';

/** Mirrors the artifact mock's pill logic: never synced at all, synced but a later push hasn't
 * landed yet, or both timestamps present (assumed in sync — the endpoint doesn't diff payloads). */
export function provenanceSyncState(p: SageSyncProvenance | null | undefined): ProvenanceSyncState {
  if (!p || !p.sageLastSyncedAt) return 'never-synced';
  if (!p.sageLastPushedAt) return 'push-pending';
  return 'in-sync';
}

// ---- Dashboard work columns ----

export function parseSageSnapshot(snapshot: string | null | undefined): any {
  if (!snapshot) return null;
  try {
    return JSON.parse(snapshot);
  } catch {
    return null;
  }
}

/** A Sage item that holds no stock. Strict on false: snapshots without the flag are stock items. */
export function isSageService(raw: any): boolean {
  return raw?.Physical === false;
}

export function isAivorService(record: any): boolean {
  return (record?.itemTypeID ?? record?.ItemTypeID) === ItemType.Service;
}

/** Mirrors SageItemPayloadHelpers.IsKeptOutOfSage. */
export function isKeptOutOfSage(record: any): boolean {
  return (record?.itemTypeID ?? record?.ItemTypeID) === ItemType.VariableItem;
}

/** A Sage service only ever matches an Aivor service, and a stock item only a stock item. */
export function sameItemKind(sageRaw: any, aivorRecord: any): boolean {
  return isSageService(sageRaw) === isAivorService(aivorRecord);
}

/** Display name for a match's Sage side. Bundles carry no Name, only Description/BundleCode. */
export function sageSnapshotName(
  snapshot: string | null | undefined,
  entityType: SageEntityTypeName | string
): string {
  const raw = parseSageSnapshot(snapshot);
  if (!raw) return '—';
  if (entityType === 'ItemBundle') return raw.Description || raw.BundleCode || '—';
  return raw.Name || raw.Description || raw.name || '—';
}

export type SageReviewGroupKind =
  | 'new-in-sage'
  | 'new-in-aivor'
  | 'possible-match'
  | 'needs-uom'
  | 'ready';

/** Buckets on the raw decision, not mapDecisionToUiStatus — that helper folds NeedsUomMapping into
 * 'review', which would merge unit-mapping work into the possible-matches group. */
export function reviewGroupKind(decision: SageDecision): SageReviewGroupKind {
  switch (decision) {
    case 'ImportToAivor':
      return 'new-in-sage';
    case 'CreateInSage':
      return 'new-in-aivor';
    case 'NeedsUomMapping':
      return 'needs-uom';
    case 'Pending':
      return 'possible-match';
    default:
      return 'ready';
  }
}

export function reviewGroupLabel(kind: SageReviewGroupKind, entityLabel: string): string {
  switch (kind) {
    case 'new-in-sage':
      return `New ${entityLabel.toLowerCase()} in Sage`;
    case 'new-in-aivor':
      return `New ${entityLabel.toLowerCase()} in Aivor`;
    case 'possible-match':
      return `Possible ${entityLabel.toLowerCase()} matches`;
    case 'needs-uom':
      return `${entityLabel} needing a unit matched`;
    case 'ready':
      return `${entityLabel} ready to apply`;
  }
}

/** How a problem clears, which sets the fix panel's "how it clears" line. */
export type SageFixKind = 'aivor' | 'waits' | 'sage' | 'import' | 'link' | 'support';

/** The fix the panel offers for a reason. */
export type SageFix =
  | 'units'
  | 'sales-group'
  | 'refresh-groups'
  | 'code'
  | 'name'
  | 'categories'
  | 'rematch'
  | 'sync'
  | 'sync-all'
  | 'bills-of-materials'
  | 'support'
  | 'review'
  | 'matches';

export interface SageActionableErrorCopy {
  entityPill: string;
  label: string;
  whatToDo: string;
  /** The column button's label; null for "See what to do". */
  action: string | null;
  kind: SageFixKind;
}

export const SAGE_FIX_CLEARS: Record<SageFixKind, string> = {
  aivor: "Once it's fixed in Aivor, it drops off this list.",
  waits: 'It clears once the record it depends on is in Sage. Syncs send that first.',
  sage: 'It clears on the next sync after the change is made.',
  import: 'It clears once the record is brought into Aivor.',
  link: 'It clears once the match is corrected.',
  support: 'Only Aivor support can fix this.',
};

const FINISHED_GOODS = 'Item bundles';

/** Plain-language copy per actionable error code. A code with no entry gets `actionableErrorCopy`'s fallback. */
export const SAGE_ACTIONABLE_ERROR_COPY: Record<string, SageActionableErrorCopy> = {
  unmapped_category: {
    entityPill: 'Items',
    label: "Item's category isn't in Sage yet",
    whatToDo: 'Its category has to reach Sage first. Sync everything to send both, in that order',
    action: 'Sync everything now',
    kind: 'waits',
  },
  unmapped_supplier_category: {
    entityPill: 'Suppliers',
    label: "Supplier's category isn't in Sage yet",
    whatToDo: 'Its category has to reach Sage first. Sync everything to send both, in that order',
    action: 'Sync everything now',
    kind: 'waits',
  },
  unmapped_reporting_group: {
    entityPill: 'Items',
    label: 'No sales group chosen',
    whatToDo: 'Choose the sales group Sage records these items under',
    action: 'Choose a sales group',
    kind: 'aivor',
  },
  unsynced_reporting_group: {
    entityPill: 'Items',
    label: "Sales group hasn't come through from Sage",
    whatToDo: 'Refresh sales groups from Sage, then sync items again',
    action: 'Refresh sales groups',
    kind: 'waits',
  },
  // Bills of materials are only edited in Manufacturing.
  no_active_bom: {
    entityPill: FINISHED_GOODS,
    label: 'Finished good has no active bill of materials',
    whatToDo: 'Create or activate its bill of materials in Aivor',
    action: 'Open bills of materials',
    kind: 'aivor',
  },
  component_not_linked: {
    entityPill: FINISHED_GOODS,
    label: "An item it's made from isn't in Sage yet",
    whatToDo: "The items it's made from have to reach Sage first. Sync everything to send them, in that order",
    action: 'Sync everything now',
    kind: 'waits',
  },
  bundle_code_missing: {
    entityPill: FINISHED_GOODS,
    label: 'Finished good has no item code',
    whatToDo: 'Give it an item code in Aivor; Sage needs one to save it',
    action: null,
    kind: 'aivor',
  },
  bundle_code_duplicate: {
    entityPill: FINISHED_GOODS,
    label: 'Two finished goods share an item code',
    whatToDo: 'Change one of the item codes so they differ',
    action: 'Change the codes',
    kind: 'aivor',
  },
  // Sage's own codes aren't checked as you type, so this one clears on the next sync.
  bundle_code_collides_with_sage: {
    entityPill: FINISHED_GOODS,
    label: 'Item code already used in Sage',
    whatToDo: 'Change the item code to one Sage doesn\'t use, then sync finished goods',
    action: 'Change the code',
    kind: 'sage',
  },
  bundle_code_too_long: {
    entityPill: FINISHED_GOODS,
    label: 'Item code or name too long for Sage',
    whatToDo: "Shorten the item code to fit Sage's limit",
    action: 'Shorten the code',
    kind: 'aivor',
  },
  bundle_description_too_long: {
    entityPill: FINISHED_GOODS,
    label: 'Item code or name too long for Sage',
    whatToDo: "Shorten the name to fit Sage's limit",
    action: 'Shorten the name',
    kind: 'aivor',
  },
  // Aivor sends a component's own selling price when it has one, so either side fixes it.
  component_price_missing: {
    entityPill: FINISHED_GOODS,
    label: "Items it's made from have no price",
    whatToDo: 'Add a selling price for those items in Sage or Aivor, then sync finished goods',
    action: null,
    kind: 'sage',
  },
  old_record_still_active: {
    entityPill: FINISHED_GOODS,
    label: 'Old Sage record still active',
    whatToDo: "This finished good now goes to Sage in its new form, but Aivor couldn't make the old Sage record inactive. Make it inactive in Sage, then sync finished goods",
    action: null,
    kind: 'sage',
  },
  item_kind_mismatch: {
    entityPill: 'Items',
    label: 'Service and stock item mixed up',
    whatToDo: 'One side has this as a service and the other as a stock item. Make them the same kind, or give the Aivor item a different code, then sync items',
    action: 'Change the code',
    kind: 'sage',
  },
  // Its button opens the units section, not a tab.
  NeedsUomMapping: {
    entityPill: 'Items',
    label: "Sage unit isn't in Aivor",
    whatToDo: 'Choose the Aivor unit for each item, then apply',
    action: 'Choose units',
    kind: 'import',
  },
  ItemCategoryUnresolved: {
    entityPill: 'Items',
    label: 'Category not brought in from Sage yet',
    whatToDo: 'Bring each category into Aivor, or use one Aivor already has, then apply these items again',
    action: 'Sort out the categories',
    kind: 'import',
  },
  BundleComponentUnresolved: {
    entityPill: FINISHED_GOODS,
    label: "An item it's made from isn't in Aivor yet",
    whatToDo: 'Bring in those items first, then apply the finished good again',
    action: 'Review items',
    kind: 'import',
  },
  // Logged for every record type, so its pill and its sync button come from the records.
  outbound_pending: {
    entityPill: '',
    label: 'Waiting to send to Sage',
    whatToDo: "Aivor's changes haven't reached Sage yet. Sync now, or the next sync sends them",
    action: null,
    kind: 'waits',
  },
  VariableItemKeptOut: {
    entityPill: 'Items',
    label: "Variable item can't be matched",
    whatToDo: 'Variable items are kept out of Sage for now. Match the Sage item to another Aivor item, or add it to Aivor as new',
    action: 'Fix the match',
    kind: 'link',
  },
  ItemKindMismatch: {
    entityPill: 'Items',
    label: 'Service matched to a stock item',
    whatToDo: 'Match it to an Aivor record of the same kind, or add it to Aivor as new',
    action: 'Fix the match',
    kind: 'link',
  },
  DuplicateSageLink: {
    entityPill: '',
    label: 'Already linked to another record',
    whatToDo: 'Correct the link in Fix matches',
    action: 'Open Fix matches',
    kind: 'link',
  },
  AivorRecordAlreadyLinked: {
    entityPill: '',
    label: 'Already linked to another record',
    whatToDo: 'Correct the link in Fix matches',
    action: 'Open Fix matches',
    kind: 'link',
  },
  AivorRecordNotFound: {
    entityPill: '',
    label: 'Aivor record no longer exists',
    whatToDo: 'Match the Sage record to another Aivor record, or add it to Aivor as new',
    action: 'Fix the match',
    kind: 'link',
  },
  // Aivor has no screen for warehouses, so there's no button: support has to add one.
  NoActiveWarehouse: {
    entityPill: 'Items',
    label: 'No warehouse in Aivor for its stock',
    whatToDo: 'Sage holds stock for this item, but Aivor has no active warehouse to put it in. Contact Aivor support to set one up',
    action: null,
    kind: 'support',
  },
  NoFallbackCategoryOrUom: {
    entityPill: FINISHED_GOODS,
    label: 'No category or unit in Aivor',
    whatToDo: 'Aivor needs at least one category and one unit to bring this finished good in. Contact Aivor support',
    action: null,
    kind: 'support',
  },
  InvalidSnapshot: {
    entityPill: '',
    label: "Sage sent data Aivor couldn't read",
    whatToDo: 'Contact Aivor support',
    action: null,
    kind: 'support',
  },
};

/** The copy for a code, or a plain fallback: never the raw server message. */
export function actionableErrorCopy(code: string | null | undefined): SageActionableErrorCopy {
  return (
    (code && SAGE_ACTIONABLE_ERROR_COPY[code]) || {
      entityPill: '',
      label: issueReasonLabel(code) || 'Other problem',
      whatToDo: 'Open each record to see what happened',
      action: null,
      kind: 'sage',
    }
  );
}

/** The fix the panel offers for each code. A code with no entry shows its records and guidance only. */
export const SAGE_FIX: Record<string, SageFix> = {
  NeedsUomMapping: 'units',
  unmapped_reporting_group: 'sales-group',
  unsynced_reporting_group: 'refresh-groups',
  unmapped_category: 'sync-all',
  unmapped_supplier_category: 'sync-all',
  component_not_linked: 'sync-all',
  no_active_bom: 'bills-of-materials',
  bundle_code_duplicate: 'code',
  bundle_code_collides_with_sage: 'code',
  bundle_code_too_long: 'code',
  bundle_description_too_long: 'name',
  item_kind_mismatch: 'code',
  component_price_missing: 'sync',
  old_record_still_active: 'sync',
  outbound_pending: 'sync',
  ItemCategoryUnresolved: 'categories',
  BundleComponentUnresolved: 'review',
  // These records still wait in a review, so Fix matches (linked records only) can't see them.
  VariableItemKeptOut: 'rematch',
  ItemKindMismatch: 'rematch',
  AivorRecordNotFound: 'rematch',
  DuplicateSageLink: 'matches',
  AivorRecordAlreadyLinked: 'matches',
  NoActiveWarehouse: 'support',
  NoFallbackCategoryOrUom: 'support',
  InvalidSnapshot: 'support',
};

/** Fixes made in Aivor that still have to reach Sage, so a fixed reason, or one changed in the
 * panel, offers its sync. A code Sage already uses only clears on that sync. */
const SYNCS_ONCE_FIXED: ReadonlySet<SageFix> = new Set(['sales-group', 'refresh-groups', 'code', 'name', 'bills-of-materials', 'sync', 'sync-all']);

const SYNC_LABEL: Record<SageEntityTypeName, string> = {
  SupplierCategory: 'supplier categories',
  Supplier: 'suppliers',
  ItemCategory: 'item categories',
  Item: 'items',
  ItemBundle: 'finished goods',
};

/** One sync button: a record type, or null for everything (dependencies first). */
export interface SyncTarget {
  type: SageEntityTypeName | null;
  label: string;
}

/** The record types of a reason's records, in apply order; a type the console can't sync is left out. */
export function syncTargetsFor(fix: SageFix | null, entityTypes: readonly string[], fixed = false): SyncTarget[] {
  if (!fix || !(fixed ? SYNCS_ONCE_FIXED.has(fix) : fix === 'sync' || fix === 'sync-all')) return [];
  if (fix === 'sync-all') return [{ type: null, label: 'Sync everything now' }];
  return ENTITY_ROUTE_ORDER.filter((e) => entityTypes.includes(e.type)).map((e) => ({ type: e.type, label: `Sync ${SYNC_LABEL[e.type]} now` }));
}

/** The number the sync calls use for a record type name; the inverse of `entityTypeName`. */
export function entityTypeNumber(type: SageEntityTypeName): number {
  return Number(Object.keys(SAGE_ENTITY_TYPE_BY_NUMBER).find((n) => Number(n) !== INVENTORY_TRANSACTION_ENTITY_TYPE && SAGE_ENTITY_TYPE_BY_NUMBER[Number(n)] === type));
}

const FIXED_TEXT: Partial<Record<SageFix, string>> = {
  units: 'The items are in Aivor.',
  categories: 'The items are in Aivor.',
  rematch: 'The records are in Aivor.',
};

/** What happens next once a reason has gone from the list; `synced` when the panel's own sync ran. */
export function fixedText(fix: SageFix | null, synced = false): string {
  if (synced) return 'Synced. Sage has the changes now.';
  return (fix && FIXED_TEXT[fix]) || 'Nothing in Aivor stops it now. It goes to Sage on the next sync, or sync now.';
}

// Error codes (Sage's error categories and local block codes) and the normalised categories share
// one map: their spellings never overlap.
const ISSUE_REASON_LABEL: Record<string, string> = {
  BadRequest: 'Rejected by Sage',
  AuthenticationFailed: 'Sage login problem',
  Forbidden: 'No access in Sage',
  MethodNotAllowed: 'No access in Sage',
  RateLimited: 'Sage request limit',
  LoginBlocked: 'Sage login blocked',
  SageServerError: 'Sage error',
  UnsupportedMediaType: 'Sage error',
  GatewayTimeout: 'Sage timed out',
  NetworkFailure: "Couldn't reach Sage",
  SubscriptionExpired: 'Sage subscription expired',
  NotFound: 'Not found in Sage',
  Conflict: 'In use in Sage',
  DuplicateName: 'Name already in Sage',
  outbound_pending: 'Waiting to send',
  unmapped_category: 'Category not in Sage',
  unmapped_supplier_category: 'Supplier category not in Sage',
  unmapped_reporting_group: 'No sales group',
  unsynced_reporting_group: 'Sales group missing',
  no_active_bom: 'No bill of materials',
  component_not_linked: 'Component not in Sage',
  bundle_code_missing: 'No item code',
  bundle_code_duplicate: 'Item code used twice',
  bundle_code_collides_with_sage: 'Item code taken in Sage',
  bundle_code_too_long: 'Item code too long',
  bundle_description_too_long: 'Name too long',
  old_record_still_active: 'Old Sage record still active',
  NoActiveWarehouse: 'No warehouse',
  DuplicateSageLink: 'Already linked',
  AivorRecordAlreadyLinked: 'Already linked',
  AivorRecordNotFound: 'Aivor record missing',
  ItemCategoryUnresolved: 'Category not imported',
  NeedsUomMapping: 'Unit not matched',
  NonPhysicalItem: 'Service item',
  item_kind_mismatch: 'Service and stock item mixed up',
  ItemKindMismatch: 'Service matched to a stock item',
  VariableItemKeptOut: 'Variable item not matched',
  NoFallbackCategoryOrUom: 'No default category or unit',
  BundleComponentUnresolved: 'Component not imported',
  missing_data: 'Missing details',
  authentication: 'Sage login problem',
  permission: 'No access in Sage',
  subscription_expired: 'Sage subscription expired',
  link_broken: 'Not found in Sage',
  rate_limited: 'Sage request limit',
  login_blocked: 'Sage login blocked',
  connection: "Couldn't reach Sage",
  sage_error: 'Sage error',
  data_error: 'Rejected by Sage',
  blocked: 'Needs a fix in Aivor',
  unknown: 'Other problem',
};

/** Unknown codes fall back to the category, so a code like an exception's type name never reaches
 * the screen. */
export function issueReasonLabel(code: string | null | undefined, category?: string | null): string {
  if (code && ISSUE_REASON_LABEL[code]) return ISSUE_REASON_LABEL[code];
  if (category && ISSUE_REASON_LABEL[category]) return ISSUE_REASON_LABEL[category];
  return code || category ? 'Other problem' : '';
}

/** Relative day label for a detection timestamp — Today / Yesterday / weekday, plus the time. */
export function relativeDayLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';

  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const days = Math.floor((startOfToday.getTime() - new Date(d).setHours(0, 0, 0, 0)) / 86400000);

  if (days <= 0) return `Today ${time}`;
  if (days === 1) return `Yesterday ${time}`;
  if (days < 7) return `${d.toLocaleDateString(undefined, { weekday: 'long' })} ${time}`;
  return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${time}`;
}

/** True minus sign, not a hyphen, so deltas line up in tabular figures. */
export function signedQuantity(n: number): string {
  const rounded = Math.round(n * 1000) / 1000;
  return rounded < 0 ? `−${Math.abs(rounded)}` : `+${rounded}`;
}

/** Best-effort scenario label derived client-side from the mix of matches, since the backend has no scenario field. */
export function deriveScenarioLabel(allMatches: SageReconciliationMatch[][]): string {
  const flat = allMatches.flat();
  if (flat.length === 0) {
    return 'Awaiting first analysis';
  }
  const counts = computeEntityCounts(flat);
  const sageSideEmpty = counts.newInAivor > 0 && counts.newInSage === 0 && counts.matched === 0;
  const aivorSideEmpty = counts.newInSage > 0 && counts.newInAivor === 0 && counts.matched === 0;
  if (sageSideEmpty) return 'Sage company is empty';
  if (aivorSideEmpty) return 'AIVOR is empty';
  return 'Records found in both systems';
}
